import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  IFinancialAuditInput,
  IFinancialIncome,
  IFinancialLoan,
  IFinancialShare,
  IFinancialTax,
} from "../../exports/interfaces";
// import { dynamicSuccess } from "../../hooks/useToast";
import Select from "react-select";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import { Upload } from "lucide-react";
// import { useOption } from "../../context/OptionContext";
import {
  convertNepaliToEnglishDate,
  formatDateBySetting,
  renderExistingFilePreview,
  renderFilePreview,
  validateFileSize,
} from "../../exports/functions";
import NepaliCalendar from "../NepaliCalendar";
import useMobile from "../../hooks/useMobile";
import { useSettings } from "../../context/SettingsContext";
import { financialInstitutionOptions } from "../../exports/nepalFinancialInstitutions";

const SelectWithError = Select as unknown as React.ComponentType<any>;

const Financials = ({
  financialsactiveTab,
  currentStep,
  shareFinancial,
  setShareFinancial,
  loanFinancial,
  setLoanFinancial,
  incomeFinancial,
  setIncomeFinancial,
  auditFinancial,
  setAuditFinancial,
  taxFinancial,
  setTaxFinancial,
  errorsShare,
  errorsLoan,
  errorsIncome,
  errorsAudit,
  errorsTax,
}: {
  financialsactiveTab: string;
  currentStep: number;
  shareFinancial: IFinancialShare;
  setShareFinancial: Dispatch<SetStateAction<IFinancialShare>>;
  loanFinancial: IFinancialLoan;
  setLoanFinancial: Dispatch<SetStateAction<IFinancialLoan>>;
  incomeFinancial: IFinancialIncome;
  setIncomeFinancial: Dispatch<SetStateAction<IFinancialIncome>>;
  auditFinancial: IFinancialAuditInput;
  setAuditFinancial: Dispatch<SetStateAction<IFinancialAuditInput>>;
  taxFinancial: IFinancialTax;
  setTaxFinancial: Dispatch<SetStateAction<IFinancialTax>>;
  errorsShare: any;
  errorsLoan: any;
  errorsIncome: any;
  errorsAudit: any;
  errorsTax: any;
}) => {
  // const { categories } = useOption();
  const { generalSettings } = useSettings();
  const isMobile = useMobile();
  const agmDateDropdownRef = useRef<HTMLDivElement>(null);
  const agmNepaliDateDropdownRef = useRef<HTMLDivElement>(null);
  const [showAgmCalendar, setShowAgmCalendar] = useState(false);
  const [showAgmNepaliCalendar, setShowAgmNepaliCalendar] = useState(false);
  const [nepaliAgmDate, setNepaliAgmDate] = useState("");

  useEffect(() => {
    if (auditFinancial) {
      setNepaliAgmDate(auditFinancial.agmNepaliDate || "");
    }
  }, [auditFinancial]);

  const handleSelectChange = (
    name: keyof IFinancialShare,
    selected: SelectOption | null,
  ) => {
    setShareFinancial((prev) => ({
      ...prev,
      [name]: selected?.value,
    }));
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        agmDateDropdownRef.current &&
        !agmDateDropdownRef.current.contains(event.target as Node)
      ) {
        setShowAgmCalendar(false);
      }
      if (
        agmNepaliDateDropdownRef.current &&
        !agmNepaliDateDropdownRef.current.contains(event.target as Node)
      ) {
        setShowAgmNepaliCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const previewFile = auditFinancial.file;
  const previewDocumentUrl = auditFinancial.documentUrl;
  const previewFileName = auditFinancial.fileName || previewDocumentUrl || "";

  const parseNullableNumber = (value: string) => {
    if (value === "") return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const getTotalShareMembers = (
    maleMembers: number | null,
    femaleMembers: number | null,
    otherMembers: number | null,
    institutionalMembers: number | null,
  ) => {
    const rawValues = [
      maleMembers,
      femaleMembers,
      otherMembers,
      institutionalMembers,
    ] as Array<number | null | undefined>;
    const hasValue = rawValues.some(
      (value) => value !== null && value !== undefined,
    );

    if (!hasValue) return null;

    return rawValues.reduce<number>((sum, value) => {
      const parsedValue = Number(value);
      return sum + (Number.isNaN(parsedValue) ? 0 : parsedValue);
    }, 0);
  };

  const getTotalSavingAmount = (
    totalGeneralSaving: string | number | null,
    totalRegularSaving: string | number | null,
    totalPeriodicSaving: string | number | null,
  ) => {
    const rawValues = [
      totalGeneralSaving,
      totalRegularSaving,
      totalPeriodicSaving,
    ] as Array<string | number | null | undefined>;
    const hasValue = rawValues.some(
      (value) => value !== null && value !== undefined && value !== "",
    );

    if (!hasValue) return null;

    return rawValues.reduce<number>((sum, value) => {
      const parsedValue = Number(value);
      return sum + (Number.isNaN(parsedValue) ? 0 : parsedValue);
    }, 0);
  };

  const getTotalDepositors = (
    femaleDepositors: string | number | null,
    maleDepositors: string | number | null,
    otherDepositors: string | number | null,
  ) => {
    const rawValues = [
      femaleDepositors,
      maleDepositors,
      otherDepositors,
    ] as Array<string | number | null | undefined>;
    const hasValue = rawValues.some(
      (value) => value !== null && value !== undefined && value !== "",
    );

    if (!hasValue) return null;

    return rawValues.reduce<number>((sum, value) => {
      const parsedValue = Number(value);
      return sum + (Number.isNaN(parsedValue) ? 0 : parsedValue);
    }, 0);
  };

  const getBalanceSheet = (
    totalLiabilities: string | number | null,
    totalAssets: string | number | null,
  ) => {
    const rawValues = [totalLiabilities, totalAssets] as Array<
      string | number | null | undefined
    >;
    const hasValue = rawValues.some(
      (value) => value !== null && value !== undefined && value !== "",
    );

    if (!hasValue) return null;

    return rawValues.reduce<number>((sum, value) => {
      const parsedValue = Number(value);
      return sum + (Number.isNaN(parsedValue) ? 0 : parsedValue);
    }, 0);
  };

  const getTlaToTaRatio = (
    totalLiquidAssets: string | number | null,
    totalAssets: string | number | null,
  ) => {
    if (
      totalLiquidAssets === "" ||
      totalAssets === "" ||
      totalLiquidAssets === null ||
      totalAssets === null ||
      totalLiquidAssets === undefined ||
      totalAssets === undefined
    ) {
      return null;
    }

    const tla = Number(totalLiquidAssets);
    const ta = Number(totalAssets);

    if (Number.isNaN(tla) || Number.isNaN(ta) || ta === 0) {
      return null;
    }

    const ratio = tla / ta;
    return Number.isFinite(ratio) ? Number(ratio.toFixed(4)) : null;
  };
  const handleShareChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setShareFinancial((prev: any) => {
      const parsedValue = parseNullableNumber(value);
      const nextValue =
        name === "maleMembers" ||
        name === "femaleMembers" ||
        name === "otherMembers" ||
        name === "institutionalMembers"
          ? parsedValue
          : name === "totalGeneralSaving" ||
              name === "totalRegularSaving" ||
              name === "totalPeriodicSaving"
            ? parsedValue === null
              ? ""
              : String(parsedValue)
            : value;

      const nextState = {
        ...prev,
        [name]: nextValue,
      };

      if (
        name === "maleMembers" ||
        name === "femaleMembers" ||
        name === "otherMembers" ||
        name === "institutionalMembers"
      ) {
        return {
          ...nextState,
          totalShareMembers: getTotalShareMembers(
            name === "maleMembers" ? nextValue : prev.maleMembers,
            name === "femaleMembers" ? nextValue : prev.femaleMembers,
            name === "otherMembers" ? nextValue : prev.otherMembers,
            name === "institutionalMembers"
              ? nextValue
              : prev.institutionalMembers,
          ),
        };
      }

      if (
        name === "totalGeneralSaving" ||
        name === "totalRegularSaving" ||
        name === "totalPeriodicSaving"
      ) {
        return {
          ...nextState,
          totalSavingAmount: getTotalSavingAmount(
            name === "totalGeneralSaving" ? nextValue : prev.totalGeneralSaving,
            name === "totalRegularSaving" ? nextValue : prev.totalRegularSaving,
            name === "totalPeriodicSaving"
              ? nextValue
              : prev.totalPeriodicSaving,
          ),
        };
      }

      if (
        name === "femaleDepositors" ||
        name === "maleDepositors" ||
        name === "otherDepositors"
      ) {
        return {
          ...nextState,
          totalDepositors: getTotalDepositors(
            name === "femaleDepositors" ? nextValue : prev.femaleDepositors,
            name === "maleDepositors" ? nextValue : prev.maleDepositors,
            name === "otherDepositors" ? nextValue : prev.otherDepositors,
          ),
        };
      }

      if (
        name === "totalLiabilities" ||
        name === "totalAssets" ||
        name === "totalLiquidAssets"
      ) {
        return {
          ...nextState,
          balanceSheet: getBalanceSheet(
            name === "totalLiabilities" ? nextValue : prev.totalLiabilities,
            name === "totalAssets" ? nextValue : prev.totalAssets,
          ),
          tlaToTaRatio: getTlaToTaRatio(
            name === "totalLiquidAssets" ? nextValue : prev.totalLiquidAssets,
            name === "totalAssets" ? nextValue : prev.totalAssets,
          ),
        };
      }

      return nextState;
    });
  };

  // const fiscalYearOptions =
  //   categories
  //     ?.find((item) => item.name === "Fiscal Year")
  //     ?.dropdowns?.filter((item) => item.status === "Active")
  //     ?.sort((a, b) => a.order - b.order)
  //     ?.map((item) => ({
  //       value: item.label,
  //       label: item.label,
  //     })) || [];

  const getTotalBorrowers = (
    maleBorrowers: string | number | null,
    femaleBorrowers: string | number | null,
    othersBorrowers: string | number | null,
  ) => {
    const rawValues = [
      maleBorrowers,
      femaleBorrowers,
      othersBorrowers,
    ] as Array<string | number | null | undefined>;
    const hasValue = rawValues.some(
      (value) => value !== null && value !== undefined && value !== "",
    );

    if (!hasValue) return null;

    return rawValues.reduce<number>((sum, value) => {
      const parsedValue = Number(value);
      return sum + (Number.isNaN(parsedValue) ? 0 : parsedValue);
    }, 0);
  };

  const getTotalLoanLossProvision = (
    llpPassLoan: string | number | null,
    llpSubStandardLoan: string | number | null,
    llpDoubtfulLoan: string | number | null,
    llpLossLoan: string | number | null,
  ) => {
    const rawValues = [
      llpPassLoan,
      llpSubStandardLoan,
      llpDoubtfulLoan,
      llpLossLoan,
    ] as Array<string | number | null | undefined>;
    const hasValue = rawValues.some(
      (value) => value !== null && value !== undefined && value !== "",
    );

    if (!hasValue) return null;

    return rawValues.reduce<number>((sum, value) => {
      const parsedValue = Number(value);
      return sum + (Number.isNaN(parsedValue) ? 0 : parsedValue);
    }, 0);
  };

  const getTotalNonPerformingLoan = (
    coreCapital: string | number | null,
    supplementaryCapital: string | number | null,
  ) => {
    const rawValues = [coreCapital, supplementaryCapital] as Array<
      string | number | null | undefined
    >;
    const hasValue = rawValues.some(
      (value) => value !== null && value !== undefined && value !== "",
    );

    if (!hasValue) return null;

    return rawValues.reduce<number>((sum, value) => {
      const parsedValue = Number(value);
      return sum + (Number.isNaN(parsedValue) ? 0 : parsedValue);
    }, 0);
  };

  const handleLoanChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setLoanFinancial((prev: any) => {
      const parsedValue = parseNullableNumber(value);
      const nextValue =
        name === "maleBorrowers" ||
        name === "femaleBorrowers" ||
        name === "othersBorrowers"
          ? parsedValue
          : value;

      const nextState = {
        ...prev,
        [name]: nextValue,
      };

      if (
        name === "maleBorrowers" ||
        name === "femaleBorrowers" ||
        name === "othersBorrowers"
      ) {
        return {
          ...nextState,
          totalBorrowers: getTotalBorrowers(
            name === "maleBorrowers" ? nextValue : prev.maleBorrowers,
            name === "femaleBorrowers" ? nextValue : prev.femaleBorrowers,
            name === "othersBorrowers" ? nextValue : prev.othersBorrowers,
          ),
        };
      }

      if (
        name === "llpPassLoan" ||
        name === "llpSubStandardLoan" ||
        name === "llpDoubtfulLoan" ||
        name === "llpLossLoan"
      ) {
        const calculatedLlpTotal = getTotalLoanLossProvision(
          name === "llpPassLoan" ? nextValue : prev.llpPassLoan,
          name === "llpSubStandardLoan" ? nextValue : prev.llpSubStandardLoan,
          name === "llpDoubtfulLoan" ? nextValue : prev.llpDoubtfulLoan,
          name === "llpLossLoan" ? nextValue : prev.llpLossLoan,
        );

        return {
          ...nextState,
          totalLoanLossProvision: calculatedLlpTotal,
          supplementaryCapital: calculatedLlpTotal,
          totalNonPerformingLoan: getTotalNonPerformingLoan(
            prev.coreCapital,
            calculatedLlpTotal,
          ),
        };
      }

      if (name === "coreCapital") {
        return {
          ...nextState,
          totalNonPerformingLoan: getTotalNonPerformingLoan(
            nextValue,
            prev.supplementaryCapital,
          ),
        };
      }

      return nextState;
    });
  };

  const getNetProfitLoss = (
    operationalIncome: string | number | null,
    otherIncome: string | number | null,
    operationalExpenses: string | number | null,
    otherExpenses: string | number | null,
  ) => {
    const rawValues = [
      operationalIncome,
      otherIncome,
      operationalExpenses,
      otherExpenses,
    ] as Array<string | number | null | undefined>;
    const hasValue = rawValues.some(
      (value) => value !== null && value !== undefined && value !== "",
    );

    if (!hasValue) return null;

    const parseVal = (v: string | number | null | undefined) => {
      const num = Number(v);
      return Number.isNaN(num) ? 0 : num;
    };

    const totalIncome = parseVal(operationalIncome) + parseVal(otherIncome);
    const totalExpenses =
      parseVal(operationalExpenses) + parseVal(otherExpenses);

    return totalIncome - totalExpenses;
  };

  const handleIncomeChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setIncomeFinancial((prev: any) => {
      const nextState = {
        ...prev,
        [name]: value,
      };

      if (
        name === "operationalIncome" ||
        name === "otherIncome" ||
        name === "operationalExpenses" ||
        name === "otherExpenses"
      ) {
        return {
          ...nextState,
          netProfitLoss: getNetProfitLoss(
            name === "operationalIncome" ? value : prev.operationalIncome,
            name === "otherIncome" ? value : prev.otherIncome,
            name === "operationalExpenses" ? value : prev.operationalExpenses,
            name === "otherExpenses" ? value : prev.otherExpenses,
          ),
        };
      }

      return nextState;
    });
  };

  const getParticipantsPercent = (
    participants: string | number | null | undefined,
    participantsAttended: string | number | null | undefined,
  ) => {
    if (
      participants === "" ||
      participants === null ||
      participants === undefined ||
      participantsAttended === "" ||
      participantsAttended === null ||
      participantsAttended === undefined
    ) {
      return "";
    }

    const numParticipants = Number(participants);
    const numAttended = Number(participantsAttended);

    if (
      Number.isNaN(numParticipants) ||
      Number.isNaN(numAttended) ||
      numAttended === 0
    ) {
      return "";
    }

    const result = numParticipants / numAttended;
    return Number.isFinite(result) ? Number(result.toFixed(2)).toString() : "";
  };

  useEffect(() => {
    const calculated = getParticipantsPercent(
      auditFinancial.participants,
      auditFinancial.participantsAttended,
    );
    if (calculated !== (auditFinancial.participantsPercent || "")) {
      setAuditFinancial((prev: any) => ({
        ...prev,
        participantsPercent: calculated,
      }));
    }
  }, [
    auditFinancial.participants,
    auditFinancial.participantsAttended,
    auditFinancial.participantsPercent,
    setAuditFinancial,
  ]);

  const handleAuditChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "icanNumber") {
      updatedValue = value.replace(/\D/g, "").slice(0, 18);
    }

    if (name === "auditorMobile") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    setAuditFinancial((prev: any) => {
      const nextState = {
        ...prev,
        [name]: updatedValue,
      };

      if (name === "participants" || name === "participantsAttended") {
        const nextParticipants =
          name === "participants" ? updatedValue : prev.participants;
        const nextAttended =
          name === "participantsAttended"
            ? updatedValue
            : prev.participantsAttended;

        return {
          ...nextState,
          participantsPercent: getParticipantsPercent(
            nextParticipants,
            nextAttended,
          ),
        };
      }

      return nextState;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!validateFileSize(file)) {
        e.target.value = "";
        return;
      }
      setAuditFinancial((prev) => ({
        ...prev,
        fileName: file.name,
        file: file,
        documentUrl: "",
      }));
    }
  };

  const handleTaxChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    setTaxFinancial((prev: any) => ({
      ...prev,
      [name]: value,
    }));
  };

  // const handleDraft = () => {
  //   if (financialsactiveTab === "share") {
  //     localStorage.setItem("share", JSON.stringify(shareFinancial));
  //     dynamicSuccess("Share saved as draft!");
  //   } else if (financialsactiveTab === "loan") {
  //     localStorage.setItem("loan", JSON.stringify(loanFinancial));
  //     dynamicSuccess("Loan saved as draft!");
  //   } else if (financialsactiveTab === "income") {
  //     localStorage.setItem("income", JSON.stringify(incomeFinancial));
  //     dynamicSuccess("Income saved as draft!");
  //   } else if (financialsactiveTab === "audit") {
  //     const { file, fileName, ...rest } = auditFinancial;
  //     localStorage.setItem("audit", JSON.stringify(rest));
  //     dynamicSuccess("Audit saved as draft!");
  //   }
  // };

  return (
    <>
      <div
        className={`dashboard-card ${currentStep !== 2 ? "mt-2" : ""}`}
        style={currentStep === 2 ? { borderTopLeftRadius: 0 } : {}}
      >
        <div className="row g-3">
          <div className="col-12">
            {financialsactiveTab === "share" && (
              <>
                <h5>
                  <span className="step-circle">1</span>Total Share Members,
                  Share Amount & Saving
                </h5>
                {/* <div className="row g-3">
                  <div className={`col-lg-6 col-md-4`}>
                    <label className="form-label">
                      Fiscal Year <span className="primary-color">*</span>
                    </label>
                    <SelectWithError
                      styles={
                      customSelectStyles
                      }
                      isError={Boolean(errorsShare.fiscalYear)}
                      options={fiscalYearOptions}
                      placeholder="Select the Fiscal Year"
                      value={
                        fiscalYearOptions.find(
                          (option) =>
                            option.value === shareFinancial.fiscalYear,
                        ) || null
                      }
                      onChange={(selected: SelectOption | null) => {
                        setShareFinancial((prev: any) => ({
                          ...prev,
                          fiscalYear: selected?.value,
                        }));
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    {errorsShare.fiscalYear && (
                      <p className="error-message">{errorsShare.fiscalYear}</p>
                    )}
                  </div>
                </div> */}
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">
                    Membership & Share Information
                  </h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Male Members <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.maleMembers ? "input-error" : ""}`}
                        placeholder="Enter Male Members"
                        name="maleMembers"
                        value={shareFinancial.maleMembers ?? ""}
                        onChange={handleShareChange}
                      />
                      {errorsShare.maleMembers && (
                        <p className="error-message">
                          {errorsShare.maleMembers}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Female Members <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.femaleMembers ? "input-error" : ""}`}
                        placeholder="Enter Female Members"
                        name="femaleMembers"
                        value={shareFinancial.femaleMembers ?? ""}
                        onChange={handleShareChange}
                      />
                      {errorsShare.femaleMembers && (
                        <p className="error-message">
                          {errorsShare.femaleMembers}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Other Members <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.otherMembers ? "input-error" : ""}`}
                        placeholder="Enter Others Members"
                        name="otherMembers"
                        value={shareFinancial.otherMembers ?? ""}
                        onChange={handleShareChange}
                      />
                      {errorsShare.otherMembers && (
                        <p className="error-message">
                          {errorsShare.otherMembers}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Institutional Members{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.institutionalMembers ? "input-error" : ""}`}
                        placeholder="Enter Institutional Members"
                        name="institutionalMembers"
                        value={shareFinancial.institutionalMembers ?? ""}
                        onChange={handleShareChange}
                      />
                      {errorsShare.institutionalMembers && (
                        <p className="error-message">
                          {errorsShare.institutionalMembers}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Share Members (Auto){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsShare.totalShareMembers ? "input-error" : ""}`}
                        placeholder="Auto Calculated from Members"
                        name="totalShareMembers"
                        value={shareFinancial.totalShareMembers ?? ""}
                        readOnly
                      />
                      <small className="field-description">
                        = Male + Female + Other + Institutional
                      </small>
                      {/* {errorsShare.totalShareMembers && (
                        <p className="error-message">
                          {errorsShare.totalShareMembers}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">Savings Information</h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Total General Saving{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.totalGeneralSaving ? "input-error" : ""}`}
                        placeholder="Enter Total General Saving"
                        name="totalGeneralSaving"
                        value={shareFinancial.totalGeneralSaving}
                        onChange={handleShareChange}
                      />
                      {errorsShare.totalGeneralSaving && (
                        <p className="error-message">
                          {errorsShare.totalGeneralSaving}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Regular Saving{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.totalRegularSaving ? "input-error" : ""}`}
                        placeholder="Enter Total Regular Saving"
                        name="totalRegularSaving"
                        value={shareFinancial.totalRegularSaving}
                        onChange={handleShareChange}
                      />
                      {errorsShare.totalRegularSaving && (
                        <p className="error-message">
                          {errorsShare.totalRegularSaving}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Periodic Saving{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.totalPeriodicSaving ? "input-error" : ""}`}
                        placeholder="Enter Total Periodic Saving"
                        name="totalPeriodicSaving"
                        value={shareFinancial.totalPeriodicSaving}
                        onChange={handleShareChange}
                      />
                      {errorsShare.totalPeriodicSaving && (
                        <p className="error-message">
                          {errorsShare.totalPeriodicSaving}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Saving Amount (Auto){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsShare.totalSavingAmount ? "input-error" : ""}`}
                        placeholder="Auto Calculated from Saving"
                        name="totalSavingAmount"
                        value={shareFinancial.totalSavingAmount ?? ""}
                        readOnly
                      />
                      <small className="field-description">
                        = General + Regular + Periodic
                      </small>
                      {/* {errorsShare.totalSavingAmount && (
                        <p className="error-message">
                          {errorsShare.totalSavingAmount}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">Depositor Information</h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Female Depositors{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.femaleDepositors ? "input-error" : ""}`}
                        placeholder="Enter Female Depositors"
                        name="femaleDepositors"
                        value={shareFinancial.femaleDepositors ?? ""}
                        onChange={handleShareChange}
                      />
                      {errorsShare.femaleDepositors && (
                        <p className="error-message">
                          {errorsShare.femaleDepositors}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Male Depositors <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.maleDepositors ? "input-error" : ""}`}
                        placeholder="Enter Male Depositors"
                        name="maleDepositors"
                        value={shareFinancial.maleDepositors ?? ""}
                        onChange={handleShareChange}
                      />
                      {errorsShare.maleDepositors && (
                        <p className="error-message">
                          {errorsShare.maleDepositors}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Others Depositors{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.otherDepositors ? "input-error" : ""}`}
                        placeholder="Enter Others Depositors"
                        name="otherDepositors"
                        value={shareFinancial.otherDepositors ?? ""}
                        onChange={handleShareChange}
                      />
                      {errorsShare.otherDepositors && (
                        <p className="error-message">
                          {errorsShare.otherDepositors}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Depositors (Auto){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsShare.totalDepositors ? "input-error" : ""}`}
                        placeholder="Auto Calculated from Depositors"
                        name="totalDepositors"
                        value={shareFinancial.totalDepositors ?? ""}
                        readOnly
                      />
                      <small className="field-description">
                        = Female + Male + Other
                      </small>
                      {/* {errorsShare.totalDepositors && (
                        <p className="error-message">
                          {errorsShare.totalDepositors}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">Assets & Liabilities</h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Liabilities{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.totalLiabilities ? "input-error" : ""}`}
                        placeholder="Enter Total Liabilities"
                        name="totalLiabilities"
                        value={shareFinancial.totalLiabilities}
                        onChange={handleShareChange}
                      />
                      {errorsShare.totalLiabilities && (
                        <p className="error-message">
                          {errorsShare.totalLiabilities}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Assets <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.totalAssets ? "input-error" : ""}`}
                        placeholder="Enter Total Assets"
                        name="totalAssets"
                        value={shareFinancial.totalAssets}
                        onChange={handleShareChange}
                      />
                      {errorsShare.totalAssets && (
                        <p className="error-message">
                          {errorsShare.totalAssets}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Liquid Assets (TLA){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.totalLiquidAssets ? "input-error" : ""}`}
                        placeholder="Enter Total Liquid Assets"
                        name="totalLiquidAssets"
                        value={shareFinancial.totalLiquidAssets}
                        onChange={handleShareChange}
                      />
                      {errorsShare.totalLiquidAssets && (
                        <p className="error-message">
                          {errorsShare.totalLiquidAssets}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        TLA to Total Assets (TA) Ratio (Auto){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsShare.tlaToTaRatio ? "input-error" : ""}`}
                        placeholder="Auto Calculated from TLA and TA"
                        name="tlaToTaRatio"
                        value={shareFinancial.tlaToTaRatio ?? ""}
                        onChange={handleShareChange}
                        readOnly
                      />
                      <small className="field-description">
                        = TLA ÷ Total Assets
                      </small>
                      {/* {errorsShare.tlaToTaRatio && (
                        <p className="error-message">
                          {errorsShare.tlaToTaRatio}
                        </p>
                      )} */}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Balance Sheet (Auto)
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsShare.balanceSheet ? "input-error" : ""}`}
                        placeholder="Auto Calculated from Liabilities and Assets"
                        name="balanceSheet"
                        value={shareFinancial.balanceSheet}
                        readOnly
                      />
                      <small className="field-description">
                        = Total Liabilities + Total Assets
                      </small>
                      {/* {errorsShare.balanceSheet && (
                        <p className="error-message">
                          {errorsShare.balanceSheet}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-2">
                  <h5 className="text-uppercase mb-4">Liquidity</h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Cash <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsShare.cash ? "input-error" : ""}`}
                        placeholder="Enter Cash"
                        name="cash"
                        value={shareFinancial.cash}
                        onChange={handleShareChange}
                      />
                      {errorsShare.cash && (
                        <p className="error-message">{errorsShare.cash}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Association <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errorsShare.association ? "input-error" : ""}`}
                        placeholder="Enter Association"
                        name="association"
                        value={shareFinancial.association}
                        onChange={handleShareChange}
                      />
                      {errorsShare.association && (
                        <p className="error-message">
                          {errorsShare.association}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        District Union <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errorsShare.districtUnion ? "input-error" : ""}`}
                        placeholder="Enter District Union"
                        name="districtUnion"
                        value={shareFinancial.districtUnion}
                        onChange={handleShareChange}
                      />
                      {errorsShare.districtUnion && (
                        <p className="error-message">
                          {errorsShare.districtUnion}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Bank / Cooperative{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <SelectWithError
                        styles={customSelectStyles}
                        options={financialInstitutionOptions}
                        placeholder="Select Bank / Cooperative"
                        isError={Boolean(errorsShare.bank)}
                        value={
                          financialInstitutionOptions.find(
                            (option) => option.value === shareFinancial.bank,
                          ) || null
                        }
                        onChange={(selected: SelectOption | null) =>
                          handleSelectChange("bank", selected)
                        }
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {/* <input
                        type="text"
                        className={`form-control ${errorsShare.bank ? "input-error" : ""}`}
                        placeholder="Enter Bank / Cooperative"
                        name="bank"
                        value={shareFinancial.bank}
                        onChange={handleShareChange}
                      /> */}
                      {errorsShare.bank && (
                        <p className="error-message">{errorsShare.bank}</p>
                      )}
                    </div>
                  </div>
                </div>
                {/* <div className="d-flex mt-4 justify-content-end">
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleDraft}
                  >
                    Save
                  </button>
                </div> */}
              </>
            )}
            {financialsactiveTab === "loan" && (
              <>
                <h5>
                  <span className="step-circle">2</span>Total Funds & Loan
                  Details
                </h5>
                {/* <div className="row g-3">
                  <div className={`col-lg-6 col-md-4`}>
                    <label className="form-label">
                      Fiscal Year <span className="primary-color">*</span>
                    </label>
                    <SelectWithError
                      styles={customSelectStyles}
                      options={fiscalYearOptions}
                      isError={Boolean(errorsLoan.fiscalYear)}
                      placeholder="Select the Fiscal Year"
                      value={
                        fiscalYearOptions.find(
                          (option) => option.value === loanFinancial.fiscalYear,
                        ) || null
                      }
                      onChange={(selected: SelectOption | null) => {
                        setLoanFinancial((prev: any) => ({
                          ...prev,
                          fiscalYear: selected?.value,
                        }));
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    {errorsLoan.fiscalYear && (
                      <p className="error-message">{errorsLoan.fiscalYear}</p>
                    )}
                  </div>
                </div> */}
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">Capital Information</h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Share Capital{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalShareCapital ? "input-error" : ""}`}
                        placeholder="Enter Total Share Capital"
                        name="totalShareCapital"
                        value={loanFinancial.totalShareCapital}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalShareCapital && (
                        <p className="error-message">
                          {errorsLoan.totalShareCapital}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Reserve Fund{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalReserveFund ? "input-error" : ""}`}
                        placeholder="Enter Total Reserve Fund"
                        name="totalReserveFund"
                        value={loanFinancial.totalReserveFund}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalReserveFund && (
                        <p className="error-message">
                          {errorsLoan.totalReserveFund}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Deficit Replenishment Fund{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.deficitReplenishmentFund ? "input-error" : ""}`}
                        placeholder="Enter Deficit Replenishment Fund"
                        name="deficitReplenishmentFund"
                        value={loanFinancial.deficitReplenishmentFund}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.deficitReplenishmentFund && (
                        <p className="error-message">
                          {errorsLoan.deficitReplenishmentFund}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Accumulated Profit / Loss{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.accumulatedProfitLoss ? "input-error" : ""}`}
                        placeholder="Enter Accumulated Profit / Loss"
                        name="accumulatedProfitLoss"
                        value={loanFinancial.accumulatedProfitLoss}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.accumulatedProfitLoss && (
                        <p className="error-message">
                          {errorsLoan.accumulatedProfitLoss}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Core Capital <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.coreCapital ? "input-error" : ""}`}
                        placeholder="Enter Core Capital"
                        name="coreCapital"
                        value={loanFinancial.coreCapital}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.coreCapital && (
                        <p className="error-message">
                          {errorsLoan.coreCapital}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">loan Information</h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Pass Loan <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalPassLoan ? "input-error" : ""}`}
                        placeholder="Enter Total Pass Loan"
                        name="totalPassLoan"
                        value={loanFinancial.totalPassLoan}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalPassLoan && (
                        <p className="error-message">
                          {errorsLoan.totalPassLoan}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Sub-Standard Loan{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalSubStandardLoan ? "input-error" : ""}`}
                        placeholder="Enter Total Sub-Standard Loan"
                        name="totalSubStandardLoan"
                        value={loanFinancial.totalSubStandardLoan}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalSubStandardLoan && (
                        <p className="error-message">
                          {errorsLoan.totalSubStandardLoan}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Doubtful Loan{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalDoubtfulLoan ? "input-error" : ""}`}
                        placeholder="Enter Total Doubtful Loan"
                        name="totalDoubtfulLoan"
                        value={loanFinancial.totalDoubtfulLoan}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalDoubtfulLoan && (
                        <p className="error-message">
                          {errorsLoan.totalDoubtfulLoan}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Loss Loan <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalLossLoan ? "input-error" : ""}`}
                        placeholder="Enter Total Loss Loan"
                        name="totalLossLoan"
                        value={loanFinancial.totalLossLoan}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalLossLoan && (
                        <p className="error-message">
                          {errorsLoan.totalLossLoan}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Loan Disbursed{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalLoanDisbursed ? "input-error" : ""}`}
                        placeholder="Enter Total Loan Disbursed"
                        name="totalLoanDisbursed"
                        value={loanFinancial.totalLoanDisbursed}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalLoanDisbursed && (
                        <p className="error-message">
                          {errorsLoan.totalLoanDisbursed}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        External Loan <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.externalLoan ? "input-error" : ""}`}
                        placeholder="Enter External Loan"
                        name="externalLoan"
                        value={loanFinancial.externalLoan}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.externalLoan && (
                        <p className="error-message">
                          {errorsLoan.externalLoan}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Male Borrowers <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.maleBorrowers ? "input-error" : ""}`}
                        placeholder="Enter Male Borrowers"
                        name="maleBorrowers"
                        value={loanFinancial.maleBorrowers ?? ""}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.maleBorrowers && (
                        <p className="error-message">
                          {errorsLoan.maleBorrowers}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Female Borrowers{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.femaleBorrowers ? "input-error" : ""}`}
                        placeholder="Enter Female Borrowers"
                        name="femaleBorrowers"
                        value={loanFinancial.femaleBorrowers ?? ""}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.femaleBorrowers && (
                        <p className="error-message">
                          {errorsLoan.femaleBorrowers}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Others Borrowers{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.othersBorrowers ? "input-error" : ""}`}
                        placeholder="Enter Others Borrowers"
                        name="othersBorrowers"
                        value={loanFinancial.othersBorrowers ?? ""}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.othersBorrowers && (
                        <p className="error-message">
                          {errorsLoan.othersBorrowers}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Borrowers (Auto)
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsLoan.totalBorrowers ? "input-error" : ""}`}
                        placeholder="Auto Calculated from Borrowers"
                        name="totalBorrowers"
                        value={loanFinancial.totalBorrowers ?? ""}
                        readOnly
                      />
                      <small className="field-description">
                        = Male + Female + Others Borrowers
                      </small>
                      {/* {errorsLoan.totalBorrowers && (
                        <p className="error-message">
                          {errorsLoan.totalBorrowers}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">LLP Information</h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        LLP for Pass Loan (1%){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.llpPassLoan ? "input-error" : ""}`}
                        placeholder="Enter LLP for Pass Loan (1%)"
                        name="llpPassLoan"
                        value={loanFinancial.llpPassLoan}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.llpPassLoan && (
                        <p className="error-message">
                          {errorsLoan.llpPassLoan}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        LLP for Sub-Standard Loan (25%){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.llpSubStandardLoan ? "input-error" : ""}`}
                        placeholder="Enter LLP for Sub-Standard Loan"
                        name="llpSubStandardLoan"
                        value={loanFinancial.llpSubStandardLoan}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.llpSubStandardLoan && (
                        <p className="error-message">
                          {errorsLoan.llpSubStandardLoan}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        LLP for Doubtful Loan (50%){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.llpDoubtfulLoan ? "input-error" : ""}`}
                        placeholder="Enter LLP for Doubtful Loan"
                        name="llpDoubtfulLoan"
                        value={loanFinancial.llpDoubtfulLoan}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.llpDoubtfulLoan && (
                        <p className="error-message">
                          {errorsLoan.llpDoubtfulLoan}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        LLP for Loss Loan (100%){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.llpLossLoan ? "input-error" : ""}`}
                        placeholder="Enter LLP for Loss Loan (100%)"
                        name="llpLossLoan"
                        value={loanFinancial.llpLossLoan}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.llpLossLoan && (
                        <p className="error-message">
                          {errorsLoan.llpLossLoan}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Loan Loss Provision (LLP) Amount (Auto){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsLoan.totalLoanLossProvision ? "input-error" : ""}`}
                        placeholder="Auto Calculated from LLP"
                        name="totalLoanLossProvision"
                        value={loanFinancial.totalLoanLossProvision || ""}
                        readOnly
                      />
                      <small className="field-description">
                        = LLP Pass + Sub-Standard + Doubtful + Loss
                      </small>
                      {/* {errorsLoan.totalLoanLossProvision && (
                        <p className="error-message">
                          {errorsLoan.totalLoanLossProvision}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">
                    Supplementary Capital Information
                  </h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Additional Provision of Loan Loss{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.additionalProvisionLoanLoss ? "input-error" : ""}`}
                        placeholder="Enter Additional Provision of Loan Loss"
                        name="additionalProvisionLoanLoss"
                        value={loanFinancial.additionalProvisionLoanLoss}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.additionalProvisionLoanLoss && (
                        <p className="error-message">
                          {errorsLoan.additionalProvisionLoanLoss}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Assets Revaluation Reserve{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.assetsRevaluationReserve ? "input-error" : ""}`}
                        placeholder="Enter Assets Revaluation Reserve"
                        name="assetsRevaluationReserve"
                        value={loanFinancial.assetsRevaluationReserve}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.assetsRevaluationReserve && (
                        <p className="error-message">
                          {errorsLoan.assetsRevaluationReserve}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Free Reserve <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.freeReserve ? "input-error" : ""}`}
                        placeholder="Enter Free Reserve"
                        name="freeReserve"
                        value={loanFinancial.freeReserve}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.freeReserve && (
                        <p className="error-message">
                          {errorsLoan.freeReserve}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Supplementry Capital (Auto){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsLoan.supplementaryCapital ? "input-error" : ""}`}
                        placeholder="Auto Calculated from LLP"
                        name="supplementaryCapital"
                        value={loanFinancial.supplementaryCapital || ""}
                        readOnly
                      />
                      <small className="field-description">
                        = LLP Pass + Sub-Standard + Doubtful + Loss
                      </small>
                      {/* {errorsLoan.supplementaryCapital && (
                        <p className="error-message">
                          {errorsLoan.supplementaryCapital}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">
                    Total Loan and Capital Information
                  </h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Risk Weighted Assets (RWA){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalRiskWeightedAssets ? "input-error" : ""}`}
                        placeholder="Enter Total Risk Weighted Assets"
                        name="totalRiskWeightedAssets"
                        value={loanFinancial.totalRiskWeightedAssets}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalRiskWeightedAssets && (
                        <p className="error-message">
                          {errorsLoan.totalRiskWeightedAssets}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Non Performing Loan (Auto){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsLoan.totalNonPerformingLoan ? "input-error" : ""}`}
                        placeholder="Auto Calculated from Core and Supplementary Capital"
                        name="totalNonPerformingLoan"
                        value={loanFinancial.totalNonPerformingLoan || ""}
                        readOnly
                      />
                      <small className="field-description">
                        = Core Capital + Supplementary Capital
                      </small>
                      {/* {errorsLoan.totalNonPerformingLoan && (
                        <p className="error-message">
                          {errorsLoan.totalNonPerformingLoan}
                        </p>
                      )} */}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Non Performing Loan %{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.nplPercentage ? "input-error" : ""}`}
                        placeholder="Enter Total Non Performing Loan %"
                        name="nplPercentage"
                        value={loanFinancial.nplPercentage}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.nplPercentage && (
                        <p className="error-message">
                          {errorsLoan.nplPercentage}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Capital Fund{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalCapitalFund ? "input-error" : ""}`}
                        placeholder="Enter Total Capital Fund"
                        name="totalCapitalFund"
                        value={loanFinancial.totalCapitalFund}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalCapitalFund && (
                        <p className="error-message">
                          {errorsLoan.totalCapitalFund}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Core Capital Ratio{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.coreCapitalRatio ? "input-error" : ""}`}
                        placeholder="Enter Core Capital Ratio"
                        name="coreCapitalRatio"
                        value={loanFinancial.coreCapitalRatio}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.coreCapitalRatio && (
                        <p className="error-message">
                          {errorsLoan.coreCapitalRatio}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Capital Fund Ratio{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalCapitalFundRatio ? "input-error" : ""}`}
                        placeholder="Enter Total Capital Fund Ratio"
                        name="totalCapitalFundRatio"
                        value={loanFinancial.totalCapitalFundRatio}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalCapitalFundRatio && (
                        <p className="error-message">
                          {errorsLoan.totalCapitalFundRatio}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Borrowing <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.totalBorrowing ? "input-error" : ""}`}
                        placeholder="Enter Total Borrowing"
                        name="totalBorrowing"
                        value={loanFinancial.totalBorrowing}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.totalBorrowing && (
                        <p className="error-message">
                          {errorsLoan.totalBorrowing}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total No of Loan File Re-Scheduled{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (["-", "e", "+", "."].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.loanRescheduledCount ? "input-error" : ""}`}
                        placeholder="Enter Total No of Loan File Re-Scheduled"
                        name="loanRescheduledCount"
                        value={loanFinancial.loanRescheduledCount ?? ""}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.loanRescheduledCount && (
                        <p className="error-message">
                          {errorsLoan.loanRescheduledCount}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Total Amount of Loan Re-Scheduled{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.loanRescheduledAmount ? "input-error" : ""}`}
                        placeholder="Enter Total Amount of Loan Re-Scheduled"
                        name="loanRescheduledAmount"
                        value={loanFinancial.loanRescheduledAmount}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.loanRescheduledAmount && (
                        <p className="error-message">
                          {errorsLoan.loanRescheduledAmount}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Investment in Pvt. Ltd{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.investmentPrivateLtd ? "input-error" : ""}`}
                        placeholder="Enter Investment in Pvt. Ltd"
                        name="investmentPrivateLtd"
                        value={loanFinancial.investmentPrivateLtd}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.investmentPrivateLtd && (
                        <p className="error-message">
                          {errorsLoan.investmentPrivateLtd}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Investment in House / Building{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsLoan.investmentHouseBuilding ? "input-error" : ""}`}
                        placeholder="Enter Investment in House / Building"
                        name="investmentHouseBuilding"
                        value={loanFinancial.investmentHouseBuilding}
                        onChange={handleLoanChange}
                      />
                      {errorsLoan.investmentHouseBuilding && (
                        <p className="error-message">
                          {errorsLoan.investmentHouseBuilding}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                {/* <div className="d-flex mt-4 justify-content-end">
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleDraft}
                  >
                    Save
                  </button>
                </div> */}
              </>
            )}
            {financialsactiveTab === "income" && (
              <>
                <h5>
                  <span className="step-circle">3</span>Income Expenses & Other
                  Details
                </h5>
                {/* <div className="row g-3">
                  <div className={`col-lg-6 col-md-4`}>
                    <label className="form-label">
                      Fiscal Year <span className="primary-color">*</span>
                    </label>
                    <SelectWithError
                      styles={customSelectStyles}
                      options={fiscalYearOptions}
                      isError={Boolean(errorsIncome.fiscalYear)}
                      placeholder="Select the Fiscal Year"
                      value={
                        fiscalYearOptions.find(
                          (option) =>
                            option.value === incomeFinancial.fiscalYear,
                        ) || null
                      }
                      onChange={(selected: SelectOption | null) => {
                        setIncomeFinancial((prev: any) => ({
                          ...prev,
                          fiscalYear: selected?.value,
                        }));
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    {errorsIncome.fiscalYear && (
                      <p className="error-message">{errorsIncome.fiscalYear}</p>
                    )}
                  </div>
                </div> */}
                <div className="dashboard-card mt-3">
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Operational Income{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsIncome.operationalIncome ? "input-error" : ""}`}
                        placeholder="Enter Operational Income"
                        name="operationalIncome"
                        value={incomeFinancial.operationalIncome}
                        onChange={handleIncomeChange}
                      />
                      {errorsIncome.operationalIncome && (
                        <p className="error-message">
                          {errorsIncome.operationalIncome}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Other Income <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsIncome.otherIncome ? "input-error" : ""}`}
                        placeholder="Enter Other Income"
                        name="otherIncome"
                        value={incomeFinancial.otherIncome}
                        onChange={handleIncomeChange}
                      />
                      {errorsIncome.otherIncome && (
                        <p className="error-message">
                          {errorsIncome.otherIncome}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Operational Expenses{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsIncome.operationalExpenses ? "input-error" : ""}`}
                        placeholder="Enter Operational Expenses"
                        name="operationalExpenses"
                        value={incomeFinancial.operationalExpenses}
                        onChange={handleIncomeChange}
                      />
                      {errorsIncome.operationalExpenses && (
                        <p className="error-message">
                          {errorsIncome.operationalExpenses}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Other Expenses <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsIncome.otherExpenses ? "input-error" : ""}`}
                        placeholder="Enter Other Expenses"
                        name="otherExpenses"
                        value={incomeFinancial.otherExpenses}
                        onChange={handleIncomeChange}
                      />
                      {errorsIncome.otherExpenses && (
                        <p className="error-message">
                          {errorsIncome.otherExpenses}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Net Profit / Loss (Auto){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsIncome.netProfitLoss ? "input-error" : ""}`}
                        placeholder="Auto Calculated from Income and Expenses"
                        name="netProfitLoss"
                        value={incomeFinancial.netProfitLoss ?? ""}
                        readOnly
                      />
                      <small className="field-description">
                        = (Operational + Other Income) - (Operational + Other
                        Expenses)
                      </small>
                      {/* {errorsIncome.netProfitLoss && (
                        <p className="error-message">
                          {errorsIncome.netProfitLoss}
                        </p>
                      )} */}
                    </div>
                  </div>
                </div>
                {/* <div className="d-flex mt-4 justify-content-end">
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleDraft}
                  >
                    Save
                  </button>
                </div> */}
              </>
            )}
            {financialsactiveTab === "audit" && (
              <>
                <h5>
                  <span className="step-circle">4</span>Auditor & AGM Details
                </h5>
                {/* <div className="row g-3">
                  <div className={`col-lg-6 col-md-4`}>
                    <label className="form-label">
                      Fiscal Year <span className="primary-color">*</span>
                    </label>
                    <SelectWithError
                      styles={customSelectStyles}
                      isError={Boolean(errorsAudit.fiscalYear)}
                      options={fiscalYearOptions}
                      placeholder="Select the Fiscal Year"
                      value={
                        fiscalYearOptions.find(
                          (option) =>
                            option.value === auditFinancial.fiscalYear,
                        ) || null
                      }
                      onChange={(selected: SelectOption | null) => {
                        setAuditFinancial((prev: any) => ({
                          ...prev,
                          fiscalYear: selected?.value,
                        }));
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    {errorsAudit.fiscalYear && (
                      <p className="error-message">{errorsAudit.fiscalYear}</p>
                    )}
                  </div>
                </div> */}
                <div className="dashboard-card mt-3">
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Auditor's Name <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control ${errorsAudit.auditorName ? "input-error" : ""}`}
                        placeholder="Enter Auditor's Name"
                        name="auditorName"
                        value={auditFinancial.auditorName}
                        onChange={handleAuditChange}
                      />
                      {errorsAudit.auditorName && (
                        <p className="error-message">
                          {errorsAudit.auditorName}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Auditor's Mobile Number
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="\d*"
                        maxLength={10}
                        className={`form-control ${errorsAudit.auditorMobile ? "input-error" : ""}`}
                        placeholder="Enter Auditor's Mobile Number"
                        name="auditorMobile"
                        value={auditFinancial.auditorMobile}
                        onChange={handleAuditChange}
                      />
                      {errorsAudit.auditorMobile && (
                        <p className="error-message">
                          {errorsAudit.auditorMobile}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Auditor's Address{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-control with-spin ${errorsAudit.auditorAddress ? "input-error" : ""}`}
                        placeholder="Enter Auditor's Address"
                        name="auditorAddress"
                        value={auditFinancial.auditorAddress}
                        onChange={handleAuditChange}
                      />
                      {errorsAudit.auditorAddress && (
                        <p className="error-message">
                          {errorsAudit.auditorAddress}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div
                  className="dashboard-card mt-3"
                  style={{ overflow: "unset" }}
                >
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Auditer’s Membership Number{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={18}
                        className={`form-control ${errorsAudit.icanNumber ? "input-error" : ""}`}
                        placeholder="Enter Auditer’s Membership Number"
                        name="icanNumber"
                        value={auditFinancial.icanNumber}
                        onChange={handleAuditChange}
                      />
                      {errorsAudit.icanNumber && (
                        <p className="error-message">
                          {errorsAudit.icanNumber}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4" data-field="agmDate">
                      <label className="form-label">
                        Date of AGM (Nepali)
                        <span className="primary-color">*</span>
                      </label>
                      <div
                        className="dropdown position-relative"
                        ref={agmNepaliDateDropdownRef}
                      >
                        <button
                          type="button"
                          className={`form-control text-start nepali-date-picker ${
                            errorsAudit.agmNepaliDate ? "input-error" : ""
                          }`}
                          onClick={() =>
                            setShowAgmNepaliCalendar(!showAgmNepaliCalendar)
                          }
                        >
                          <div className="d-flex justify-content-between align-items-center w-100">
                            <span className="text-truncate">
                              {formatDateBySetting(
                                nepaliAgmDate || auditFinancial.agmNepaliDate,
                                generalSettings?.dateFormat,
                              ) ||
                                generalSettings?.dateFormat ||
                                "mm/dd/yyyy"}
                            </span>

                            <i className="fa-regular fa-calendar"></i>
                          </div>
                        </button>

                        {showAgmNepaliCalendar && (
                          <div
                            className="dropdown-menu show p-0"
                            style={{
                              width: isMobile ? "249px" : "560px",
                              height: isMobile ? "407px" : "380px",
                              border: "1px solid #ddd",
                              zIndex: 1050,
                            }}
                          >
                            <NepaliCalendar
                              onDateSelect={(nepaliDate, englishDate) => {
                                const convertedEnglishDate =
                                  convertNepaliToEnglishDate(
                                    nepaliDate,
                                    englishDate,
                                  );
                                setNepaliAgmDate(nepaliDate);
                                setAuditFinancial((prev: any) => ({
                                  ...prev,
                                  agmDate: convertedEnglishDate,
                                  agmNepaliDate: nepaliDate,
                                }));
                                setShowAgmNepaliCalendar(false);
                              }}
                            />
                          </div>
                        )}
                      </div>
                      {errorsAudit.agmNepaliDate && (
                        <p className="error-message">
                          {errorsAudit.agmNepaliDate}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4" data-field="agmDate">
                      <label className="form-label">
                        Date of AGM (English)
                        <span className="primary-color">*</span>
                      </label>
                      <div
                        className="dropdown position-relative"
                        ref={agmDateDropdownRef}
                      >
                        <button
                          type="button"
                          className={`form-control text-start nepali-date-picker ${
                            errorsAudit.agmDate ? "input-error" : ""
                          }`}
                          onClick={() => setShowAgmCalendar(!showAgmCalendar)}
                        >
                          <div className="d-flex justify-content-between align-items-center w-100">
                            <span className="text-truncate">
                              {formatDateBySetting(
                                auditFinancial.agmDate,
                                generalSettings?.dateFormat,
                              ) ||
                                generalSettings?.dateFormat ||
                                "mm/dd/yyyy"}
                            </span>

                            <i className="fa-regular fa-calendar"></i>
                          </div>
                        </button>

                        {showAgmCalendar && (
                          <div
                            className="dropdown-menu show p-0"
                            style={{
                              width: isMobile ? "249px" : "560px",
                              height: isMobile ? "407px" : "380px",
                              border: "1px solid #ddd",
                              zIndex: 1050,
                              right: 0,
                              left: "auto",
                            }}
                          >
                            <NepaliCalendar
                              onDateSelect={(nepaliDate, englishDate) => {
                                const convertedEnglishDate =
                                  convertNepaliToEnglishDate(
                                    nepaliDate,
                                    englishDate,
                                  );
                                setAuditFinancial((prev: any) => ({
                                  ...prev,
                                  agmDate: convertedEnglishDate,
                                  agmNepaliDate: nepaliDate,
                                }));
                                setShowAgmCalendar(false);
                              }}
                            />
                          </div>
                        )}
                      </div>
                      {errorsAudit.agmDate && (
                        <p className="error-message">{errorsAudit.agmDate}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        No. of Participants Invited to AGM{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsAudit.participants ? "input-error" : ""}`}
                        placeholder="Enter No. of Participants Invited to AGM"
                        name="participants"
                        value={auditFinancial.participants || ""}
                        onChange={handleAuditChange}
                      />
                      {errorsAudit.participants && (
                        <p className="error-message">
                          {errorsAudit.participants}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        No. of Participants Attended the AGM{" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsAudit.participantsAttended ? "input-error" : ""}`}
                        placeholder="Enter No. Participants Attended the AGM"
                        name="participantsAttended"
                        value={auditFinancial.participantsAttended || ""}
                        onChange={handleAuditChange}
                      />
                      {errorsAudit.participantsAttended && (
                        <p className="error-message">
                          {errorsAudit.participantsAttended}
                        </p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Participants % in AGM (Auto){" "}
                        <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        className={`form-control ${errorsAudit.participantsPercent ? "input-error" : ""}`}
                        placeholder="Auto Calculated from Participants and Attendants"
                        name="participantsPercent"
                        value={auditFinancial.participantsPercent || ""}
                        readOnly
                      />
                      <small className="field-description">
                        = Participants Invited ÷ Participants Attended
                      </small>
                      {errorsAudit.participantsPercent && (
                        <p className="error-message">
                          {errorsAudit.participantsPercent}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className={`dashboard-card mt-3`}>
                  <div className="row g-3">
                    <div className="col-12">
                      <div className="mb-3">
                        <h6>Audit Documents</h6>
                        <small>Audit reports and financial statements</small>
                      </div>
                      <label className="form-label">
                        Upload Document (PDF, JPEG, PNG)
                      </label>
                      {previewFile || previewDocumentUrl ? (
                        <div className="upload-box p-4">
                          {previewFile
                            ? renderFilePreview(
                                previewFile,
                                previewFileName,
                                handleFileChange,
                              )
                            : renderExistingFilePreview(
                                previewDocumentUrl,
                                handleFileChange,
                              )}
                        </div>
                      ) : (
                        <div
                          className={`upload-box text-center p-4 ${errorsAudit.document ? "input-error" : ""}`}
                        >
                          <div className="mb-2">
                            <Upload />
                          </div>

                          <p className="mb-1">
                            Choose a file or drag and drop it here.
                          </p>

                          <small className="d-block mb-3">
                            (txt, docx, xlsx, pdf, jpeg, png - Up to 15 mb)
                          </small>

                          <label className="btn btn-badge text-main-color px-4 mb-0">
                            Browse File
                            <input
                              type="file"
                              hidden
                              name="file"
                              onChange={handleFileChange}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                    {errorsAudit.document && (
                      <p className="error-message">{errorsAudit.document}</p>
                    )}
                  </div>
                </div>
                {/* <div className="d-flex mt-4 justify-content-end">
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleDraft}
                  >
                    Save
                  </button>
                </div> */}
              </>
            )}
            {financialsactiveTab === "tax" && (
              <>
                <h5>
                  <span className="step-circle">5</span>Tax Information
                </h5>
                {/* <div className="row g-3">
                  <div className={`col-lg-6 col-md-4`}>
                    <label className="form-label">
                      Fiscal Year <span className="primary-color">*</span>
                    </label>
                    <SelectWithError
                      styles={customSelectStyles}
                      isError={Boolean(errorsTax.fiscalYear)}
                      options={fiscalYearOptions}
                      placeholder="Select the Fiscal Year"
                      value={
                        fiscalYearOptions.find(
                          (option) => option.value === taxFinancial.fiscalYear,
                        ) || null
                      }
                      onChange={(selected: SelectOption | null) => {
                        setTaxFinancial((prev: any) => ({
                          ...prev,
                          fiscalYear: selected?.value,
                        }));
                      }}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                    {errorsTax.fiscalYear && (
                      <p className="error-message">{errorsTax.fiscalYear}</p>
                    )}
                  </div>
                </div> */}
                <div className="dashboard-card mt-3">
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Income Tax <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsTax.incomeTax ? "input-error" : ""}`}
                        placeholder="Enter Income Tax"
                        name="incomeTax"
                        value={taxFinancial.incomeTax}
                        onChange={handleTaxChange}
                      />
                      {errorsTax.incomeTax && (
                        <p className="error-message">{errorsTax.incomeTax}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Interest Tax <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsTax.interestTax ? "input-error" : ""}`}
                        placeholder="Enter Interest Tax"
                        name="interestTax"
                        value={taxFinancial.interestTax}
                        onChange={handleTaxChange}
                      />
                      {errorsTax.interestTax && (
                        <p className="error-message">{errorsTax.interestTax}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Employee Tax <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsTax.employeeTax ? "input-error" : ""}`}
                        placeholder="Enter Employee Tax"
                        name="employeeTax"
                        value={taxFinancial.employeeTax}
                        onChange={handleTaxChange}
                      />
                      {errorsTax.employeeTax && (
                        <p className="error-message">{errorsTax.employeeTax}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Others <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsTax.taxOthers ? "input-error" : ""}`}
                        placeholder="Enter Any Other Required Data"
                        name="taxOthers"
                        value={taxFinancial.taxOthers}
                        onChange={handleTaxChange}
                      />
                      {errorsTax.taxOthers && (
                        <p className="error-message">{errorsTax.taxOthers}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="dashboard-card mt-3">
                  <h5 className="text-uppercase mb-4">Local Government</h5>
                  <div className="row gx-5 gy-4">
                    <div className="col-md-4">
                      <label className="form-label">
                        Service Tax <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsTax.serviceTax ? "input-error" : ""}`}
                        placeholder="Enter Service Tax"
                        name="serviceTax"
                        value={taxFinancial.serviceTax}
                        onChange={handleTaxChange}
                      />
                      {errorsTax.serviceTax && (
                        <p className="error-message">{errorsTax.serviceTax}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Rental Tax <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsTax.rentalTax ? "input-error" : ""}`}
                        placeholder="Enter Rental Tax"
                        name="rentalTax"
                        value={taxFinancial.rentalTax}
                        onChange={handleTaxChange}
                      />
                      {errorsTax.rentalTax && (
                        <p className="error-message">{errorsTax.rentalTax}</p>
                      )}
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">
                        Others <span className="primary-color">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        onKeyDown={(e) => {
                          if (e.key === "-" || e.key === "e" || e.key === "+") {
                            e.preventDefault();
                          }
                        }}
                        className={`form-control with-spin ${errorsTax.localOthers ? "input-error" : ""}`}
                        placeholder="Enter Any Other Required Data"
                        name="localOthers"
                        value={taxFinancial.localOthers}
                        onChange={handleTaxChange}
                      />
                      {errorsTax.localOthers && (
                        <p className="error-message">{errorsTax.localOthers}</p>
                      )}
                    </div>
                  </div>
                </div>
                {/* <div className="d-flex mt-4 justify-content-end">
                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleDraft}
                  >
                    Save
                  </button>
                </div> */}
              </>
            )}
          </div>
        </div>
      </div>
      {/* {financialsactiveTab === "audit" && auditData.length > 0 && (
        <AuditTable auditData={auditData} />
      )} */}
    </>
  );
};

export default Financials;
